import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useHistory } from "react-router-dom";
import { toast } from "react-toastify";
import {
  ActiveFilterChips,
  DataTable,
  InlineFilters,
  ListPageHeader,
  ListSearchForm,
  Pill,
  SearchToolbar,
  TablePagination,
  formatResultCount,
  useListPage,
} from "../../common/DataList";
import ModaleCreationOperateur from "./ModaleCreationOperateur";
import {
  STATUTS_OPERATEUR,
  etatCalendriers,
  libelleRegime,
  libelleStatut,
} from "./libellesOperateur";
import {
  ACCOMPAGNEMENT_PERSIST_KEY,
  DELAI_RECHERCHE,
  GROUPES_FILTRES_OPERATEURS,
  OPERATEURS_DEFAULT_FILTERS,
  OPERATEURS_ENDPOINT,
  REPERTOIRE_PERSIST_KEY,
  accompagnementAtoms,
  buildOperateursPayload,
  definitionsFiltresOperateurs,
  operateursFetcher,
  repertoireAtoms,
} from "./operateurListConfig";
import "./operateurs.scss";

// ============================================================
// Listes des opérateurs (CE-1404) sur le kit DataList, fidèles à la maquette
// 10 : une ligne par structure (régimes, sites, comptes, état des
// calendriers, statut). Deux vues sur la même base — « Répertoire des
// opérateurs » (tous) et « Opérateurs d'accompagnement » (au moins un
// régime) — chacune avec sa recherche mémorisée. La création s'ouvre en
// modale et propose les structures proches avant de confirmer. Le
// rattachement n'apparaît pas : toute structure est rattachée à la Caf.
// ============================================================

const COUNT_LABELS = { none: "Aucun opérateur", one: "opérateur", many: "opérateurs" };

const VUES = {
  repertoire: {
    titre: "Répertoire des opérateurs",
    sousTitre:
      "Toutes les structures vers lesquelles un contrat d'engagement peut orienter : associations, collectivités, services publics, entreprises.",
    atoms: repertoireAtoms,
    persistKey: REPERTOIRE_PERSIST_KEY,
  },
  accompagnement: {
    titre: "Opérateurs d'accompagnement",
    sousTitre:
      "Structures auxquelles une organisation délègue tout ou partie de l'accompagnement de ses allocataires, avec leurs sites, leurs calendriers et leurs comptes.",
    atoms: accompagnementAtoms,
    persistKey: ACCOMPAGNEMENT_PERSIST_KEY,
  },
};

// Compteur d'une colonne numérique : le zéro s'écrit « — », comme les autres
// absences de la ligne (régimes, calendriers).
const nombreOuTiret = (n) =>
  n ? <span className="cell-primary">{n}</span> : <span className="text-muted">—</span>;

export const getOperateurColumns = () => [
  {
    key: "operateur",
    header: "Opérateur",
    className: "col-recipient",
    render: (o) => (
      <>
        <Link to={`/operateurs/${o.id}`} className="recipient-name">
          {o.nom}
        </Link>
        {o.sigle && o.sigle !== o.nom && <span className="cell-secondary">{o.sigle}</span>}
      </>
    ),
  },
  {
    key: "regimes",
    header: "Régimes",
    render: (o) =>
      (o.regimes || []).length ? (
        <span className="pills-inline">
          {o.regimes.map((r) => (
            <Pill key={r} tone="neutral">
              {libelleRegime(r)}
            </Pill>
          ))}
        </span>
      ) : (
        <span className="text-muted">—</span>
      ),
  },
  {
    key: "nombreSites",
    header: "Sites",
    className: "col-nombre",
    render: (o) => nombreOuTiret(o.nombreSites),
  },
  {
    key: "nombreComptes",
    header: "Comptes",
    className: "col-nombre",
    render: (o) => nombreOuTiret(o.nombreComptes),
  },
  {
    key: "calendriers",
    header: "Calendriers",
    render: (o) => {
      const etat = etatCalendriers(o);
      if (!etat) return <span className="text-muted">—</span>;
      return (
        <span className={`cell-note--${etat.tone}`}>
          <i className={`fa ${etat.icon}`} aria-hidden="true" /> {etat.texte}
        </span>
      );
    },
  },
  {
    key: "statut",
    header: "Statut",
    className: "col-statut",
    render: (o) => {
      const statut = STATUTS_OPERATEUR[o.statut] || {};
      return (
        <>
          <Pill tone={statut.tone || "neutral"} icon={statut.icon}>
            {libelleStatut(o.statut)}
          </Pill>
          {o.statut === "INACTIF" && o.motifInactivite && (
            // Une ligne, tronquée : le motif complet est sur la fiche (et au survol).
            <span className="cell-secondary" title={o.motifInactivite}>
              {o.motifInactivite}
            </span>
          )}
        </>
      );
    },
  },
  {
    key: "actions",
    header: "Actions",
    headerSrOnly: true,
    className: "col-actions",
    render: (o) => (
      <Link
        to={`/operateurs/${o.id}`}
        className="btn btn-ghost btn-icon"
        aria-label={`Ouvrir la fiche de ${o.nom}`}
        title="Ouvrir la fiche"
      >
        <i className="fa fa-arrow-right" aria-hidden="true" />
      </Link>
    ),
  },
];

const OperateurList = ({ avecRegimeSeulement = false }) => {
  const vue = avecRegimeSeulement ? VUES.accompagnement : VUES.repertoire;
  const history = useHistory();
  const [modaleOuverte, setModaleOuverte] = useState(false);

  const definitions = useMemo(
    () => definitionsFiltresOperateurs({ avecRegimeSeulement }),
    [avecRegimeSeulement]
  );
  const buildPayload = useCallback(
    (search) => buildOperateursPayload(search, { avecRegimeSeulement }),
    [avecRegimeSeulement]
  );

  const list = useListPage({
    searchAtom: vue.atoms.searchAtom,
    resultsAtom: vue.atoms.resultsAtom,
    defaultFilters: OPERATEURS_DEFAULT_FILTERS,
    endpoint: OPERATEURS_ENDPOINT,
    buildPayload,
    fetcher: operateursFetcher,
    persistKey: vue.persistKey,
    definitions,
    groups: GROUPES_FILTRES_OPERATEURS,
  });

  // Recherche automatique : la liste se relance 800 ms après la dernière
  // modification d'un critère, comme les autres listes du kit.
  const soumettre = useRef(list.submit);
  soumettre.current = list.submit;
  useEffect(() => {
    if (!list.isDirty) return undefined;
    const minuterie = setTimeout(() => soumettre.current(), DELAI_RECHERCHE);
    return () => clearTimeout(minuterie);
  }, [list.filters, list.isDirty]);

  // Après la création, l'écran ouvre la fiche : c'est là que s'ajoutent les
  // sites. La liste se rechargera d'elle-même au retour (recherche relancée).
  const surCreation = (fiche) => {
    setModaleOuverte(false);
    toast.success(`L'opérateur ${fiche.nom} a été créé. Ajoutez-lui ses sites depuis sa fiche.`);
    list.runSearch(list.search);
    history.push(`/operateurs/${fiche.id}`);
  };

  const columns = useMemo(() => getOperateurColumns(), []);

  // Sans aucun critère actif, une liste vide est un répertoire encore vide :
  // l'état vide invite à créer la première structure plutôt qu'à élargir.
  const etatVide = list.canReset
    ? {
        icon: "fa-filter",
        title: "Aucun opérateur ne correspond à ces critères",
        hint: "Élargissez le régime ou le statut, ou videz la recherche.",
        action: { label: "Réinitialiser les filtres", onClick: list.resetFilters },
      }
    : {
        icon: "fa-building",
        title: "Aucun opérateur pour l'instant",
        hint: avecRegimeSeulement
          ? "Les opérateurs d'accompagnement sont les structures du répertoire qui exercent au moins un régime."
          : "Créez la première structure, puis ajoutez ses sites depuis sa fiche.",
        action: avecRegimeSeulement
          ? null
          : { label: "Créer un opérateur", onClick: () => setModaleOuverte(true) },
      };

  return (
    <div className="container-fluid px-5 my-5 list-page operateur-list">
      <ListPageHeader title={vue.titre} subtitle={vue.sousTitre}>
        <div data-priv="crud_operateurs">
          <button type="button" className="btn btn-primary" onClick={() => setModaleOuverte(true)}>
            <i className="fa fa-plus mr-2" aria-hidden="true" />
            Créer un opérateur
          </button>
        </div>
      </ListPageHeader>

      <ListSearchForm onSubmit={list.submit} aria-label="Recherche d'opérateurs">
        <SearchToolbar
          id="operateur-search"
          name="terms"
          value={list.filters.terms}
          onChange={list.onFilterChange}
          placeholder="Nom ou sigle de la structure…"
          isLoading={list.isLoading}
          isDirty={false}
          inlineFilters={
            <InlineFilters
              definitions={list.principalFilters}
              filters={list.filters}
              onChange={list.onFilterChange}
              onSet={list.setFilter}
            />
          }
          onToggleFilters={null}
        />
        <ActiveFilterChips chips={list.chips} onRemove={list.removeFilter} onClear={list.resetFilters} />
      </ListSearchForm>

      <section className="list-card" aria-label="Résultats">
        <div className="results-bar">
          <p className="results-bar__count" role="status" aria-live="polite">
            {formatResultCount(list.count, COUNT_LABELS)}
          </p>
        </div>
        <DataTable
          caption={vue.titre}
          columns={columns}
          rows={list.items}
          rowKey="id"
          isLoading={list.isLoading}
          hasSearched={list.hasSearched}
          error={list.error}
          rowClassName={(o) =>
            o.statut === "EN_PREPARATION"
              ? "row--warn"
              : o.statut === "INACTIF"
              ? "row-operateur-inactif"
              : undefined
          }
          emptyState={etatVide}
        />
        <TablePagination
          page={list.page}
          nbPages={list.nbPages}
          count={list.count}
          nbItemsByPage={list.nbItemsByPage}
          onChangePage={list.changePage}
          onChangeNbItemsByPage={list.changeNbItemsByPage}
          itemLabel="opérateurs"
        />
      </section>

      <ModaleCreationOperateur
        ouvert={modaleOuverte}
        onFermer={() => setModaleOuverte(false)}
        onCreation={surCreation}
      />
    </div>
  );
};

export const RepertoireOperateurs = () => <OperateurList />;
export const OperateursAccompagnement = () => <OperateurList avecRegimeSeulement />;

export default OperateurList;
